import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useActivityData } from "./useActivityData";
import { StateBar, StateReadout } from "./components/StateBar";
import { useLabels } from "./useLabels";
import { pct } from "./format";
import { stateColourVar } from "./stateMeta";
import type { CohortFilters } from "./reportTypes";
import type { CoverageBreakdown, CurrentStateGroup } from "./activityTypes";
import "./activity.css";

/**
 * Assessment-activity tab — coverage (done vs not), recency / overdue,
 * throughput trend, counts by cycle + sign-off, and current at-risk factors
 * rolled up by Pou and domain. Dependency-free bars; reuses StateBar for the
 * current-state proportions and the shared --state-* tokens.
 */
type Props = { filters: CohortFilters };

/** Simple proportional bar (count / max) for a single series. */
function CountBar({
  value,
  max,
  colour,
}: {
  value: number;
  max: number;
  colour: string;
}) {
  const w = max > 0 ? (value / max) * 100 : 0;
  return (
    <span className="rpt-act-bar" aria-hidden="true">
      <span
        className="rpt-act-bar__fill"
        style={{ width: `${w}%`, background: colour }}
      />
    </span>
  );
}

function CoverageRows({ rows }: { rows: CoverageBreakdown[] }) {
  const max = rows.reduce((m, r) => Math.max(m, r.total), 0);
  return (
    <ul className="rpt-act-list">
      {rows.map((r) => (
        <li key={r.value} className="rpt-act-row">
          <span className="rpt-act-row__label">{r.value}</span>
          <CountBar
            value={r.assessed}
            max={max}
            colour={stateColourVar("protective")}
          />
          <span className="rpt-act-row__value">
            {r.assessed}/{r.total}
          </span>
        </li>
      ))}
    </ul>
  );
}

function groupProportion(g: CurrentStateGroup) {
  const t = g.assessedTotal;
  return t > 0
    ? { risk: g.risk / t, change: g.change / t, protective: g.protective / t }
    : { risk: 0, change: 0, protective: 0 };
}

function CurrentStateRows({
  groups,
  label,
}: {
  groups: CurrentStateGroup[];
  label: (id: string) => string;
}) {
  return (
    <ul className="rpt-act-current">
      {groups.map((g) => (
        <li key={g.id} className="rpt-act-current__row">
          <div className="rpt-act-current__head">
            <span className="rpt-act-current__name">{label(g.id)}</span>
            <span className="rpt-act-current__n">n={g.assessedTotal}</span>
          </div>
          {g.assessedTotal > 0 ? (
            <>
              <StateBar
                proportion={groupProportion(g)}
                phaseLabel={label(g.id)}
              />
              <StateReadout
                proportion={groupProportion(g)}
                counts={{
                  risk: g.risk,
                  change: g.change,
                  protective: g.protective,
                  notAssessed: g.notAssessed,
                }}
              />
            </>
          ) : (
            <span className="rpt-act-current__empty">—</span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function ActivityView({ filters }: Props) {
  const { t } = useTranslation();
  const labels = useLabels();
  // Completed-by filter (ADR-012) — local to this tab; narrows the
  // session/current-state figures below. [] = all.
  const [completedBy, setCompletedBy] = useState<readonly string[]>([]);
  const state = useActivityData(filters, completedBy);

  if (state.kind === "loading") {
    return <p className="rpt__status">{t("reports.loading")}</p>;
  }
  if (state.kind === "forbidden") {
    return (
      <p className="rpt__status rpt__status--error">{t("reports.forbidden")}</p>
    );
  }
  if (state.kind === "error") {
    return (
      <p className="rpt__status rpt__status--error">
        {t("reports.error", { message: state.message })}
      </p>
    );
  }

  const a = state.data;
  if (a.suppressed) {
    return (
      <p className="rpt__status">
        {t("reports.suppressed", { n: a.totalWhanau })}
      </p>
    );
  }

  const coveragePct =
    a.coverage.total > 0 ? a.coverage.assessed / a.coverage.total : 0;
  const maxMonth = a.throughput.months.reduce(
    (m, x) => Math.max(m, x.sessions),
    0,
  );
  const maxBucket = a.recency.buckets.reduce((m, x) => Math.max(m, x.count), 0);
  const maxCycle = a.byCycle.reduce((m, x) => Math.max(m, x.sessions), 0);
  const maxCompleted = a.byCompletedBy.reduce(
    (m, x) => Math.max(m, x.sessions),
    0,
  );

  return (
    <div className="rpt-view">
      {state.source === "mock" && (
        <p className="rpt__notice" role="status">
          {t("reports.mockNotice")}
        </p>
      )}

      {/* Who completed the assessments (ADR-012) — filter + breakdown */}
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">{t("reports.completedBy.title")}</h2>
        <p className="rpt-panel__lead">{t("reports.completedBy.lead")}</p>
        <div
          className="rpt-act-modes"
          role="group"
          aria-label={t("reports.completedBy.filterLabel")}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: "12px",
          }}
        >
          {[
            { key: "", label: t("reports.completedBy.all") },
            { key: "kaimahi", label: t("reports.completedBy.label.kaimahi") },
            { key: "whanau", label: t("reports.completedBy.label.whanau") },
            {
              key: "collaborative",
              label: t("reports.completedBy.label.collaborative"),
            },
          ].map((opt) => {
            const active =
              opt.key === ""
                ? completedBy.length === 0
                : completedBy.includes(opt.key);
            return (
              <button
                key={opt.key || "all"}
                type="button"
                aria-pressed={active}
                onClick={() => setCompletedBy(opt.key === "" ? [] : [opt.key])}
                style={{
                  minHeight: "36px",
                  padding: "4px 14px",
                  borderRadius: "8px",
                  border: active
                    ? "1px solid var(--teal, #2a9d8f)"
                    : "1px solid var(--border, #d4d9e0)",
                  background: active
                    ? "var(--teal, #2a9d8f)"
                    : "var(--surface, #fff)",
                  color: active ? "#fff" : "var(--navy, #1d3557)",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <ul className="rpt-act-list">
          {a.byCompletedBy.map((c) => (
            <li key={c.completedBy} className="rpt-act-row">
              <span className="rpt-act-row__label">
                {t(`reports.completedBy.label.${c.completedBy}`, {
                  defaultValue: c.completedBy,
                })}
              </span>
              <CountBar
                value={c.sessions}
                max={maxCompleted}
                colour={stateColourVar("protective")}
              />
              <span className="rpt-act-row__value">{c.sessions}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Coverage */}
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">
          {t("reports.activity.coverageTitle")}
        </h2>
        <p className="rpt-panel__lead">{t("reports.activity.coverageLead")}</p>
        <p className="rpt-act-headline">
          {t("reports.activity.coverageHeadline", {
            assessed: a.coverage.assessed,
            total: a.coverage.total,
            pct: pct(coveragePct),
          })}
        </p>
        <div
          className="rpt-bar rpt-bar--tall"
          role="img"
          aria-label={t("reports.activity.coverageHeadline", {
            assessed: a.coverage.assessed,
            total: a.coverage.total,
            pct: pct(coveragePct),
          })}
        >
          {a.coverage.total > 0 && (
            <>
              <span
                className="rpt-bar__seg"
                style={{
                  width: `${coveragePct * 100}%`,
                  background: stateColourVar("protective"),
                }}
              >
                <span className="rpt-bar__seg-label">{pct(coveragePct)}</span>
              </span>
              <span
                className="rpt-bar__seg"
                style={{
                  width: `${(1 - coveragePct) * 100}%`,
                  background: stateColourVar("not-assessed"),
                }}
              />
            </>
          )}
        </div>
        <div className="rpt-act-cols">
          <div>
            <h3 className="rpt-subhead">{t("reports.filter.service")}</h3>
            <CoverageRows rows={a.coverage.byService} />
          </div>
          <div>
            <h3 className="rpt-subhead">{t("reports.filter.location")}</h3>
            <CoverageRows rows={a.coverage.byLocation} />
          </div>
        </div>
      </section>

      {/* Recency */}
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">
          {t("reports.activity.recencyTitle")}
        </h2>
        <p className="rpt-panel__lead">
          {t("reports.activity.recencyLead", { days: a.recency.thresholdDays })}
        </p>
        <ul className="rpt-act-stats">
          <li className="rpt-act-stat">
            <span
              className="rpt-act-stat__n"
              style={{ color: stateColourVar("risk") }}
            >
              {a.recency.overdue}
            </span>
            <span className="rpt-act-stat__label">
              {t("reports.activity.overdue", { days: a.recency.thresholdDays })}
            </span>
          </li>
          <li className="rpt-act-stat">
            <span className="rpt-act-stat__n">{a.recency.neverAssessed}</span>
            <span className="rpt-act-stat__label">
              {t("reports.activity.neverAssessed")}
            </span>
          </li>
        </ul>
        <ul className="rpt-act-list">
          {a.recency.buckets.map((b) => (
            <li key={b.label} className="rpt-act-row">
              <span className="rpt-act-row__label">{b.label}</span>
              <CountBar
                value={b.count}
                max={maxBucket}
                colour={stateColourVar("change")}
              />
              <span className="rpt-act-row__value">{b.count}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Throughput */}
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">
          {t("reports.activity.throughputTitle")}
        </h2>
        <p className="rpt-panel__lead">
          {t("reports.activity.throughputLead", {
            thisMonth: a.throughput.thisMonth,
            lastMonth: a.throughput.lastMonth,
          })}
        </p>
        <ul className="rpt-act-trend">
          {a.throughput.months.map((m) => (
            <li
              key={m.month}
              className="rpt-act-trend__col"
              title={`${m.month}: ${String(m.sessions)}`}
            >
              <span className="rpt-act-trend__bar-wrap">
                <span
                  className="rpt-act-trend__bar"
                  style={{
                    height: `${maxMonth > 0 ? (m.sessions / maxMonth) * 100 : 0}%`,
                  }}
                />
              </span>
              <span className="rpt-act-trend__count">{m.sessions}</span>
              <span className="rpt-act-trend__month">{m.month.slice(5)}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Cycle + sign-off */}
      <section className="rpt-panel">
        <div className="rpt-act-cols">
          <div>
            <h2 className="rpt-panel__title">
              {t("reports.activity.cycleTitle")}
            </h2>
            <ul className="rpt-act-list">
              {a.byCycle.map((c) => (
                <li key={c.cycle} className="rpt-act-row">
                  <span className="rpt-act-row__label">
                    {t(`wizard.cycle.${c.cycle}`, { defaultValue: c.cycle })}
                  </span>
                  <CountBar
                    value={c.sessions}
                    max={maxCycle}
                    colour={stateColourVar("protective")}
                  />
                  <span className="rpt-act-row__value">{c.sessions}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="rpt-panel__title">
              {t("reports.activity.signoffTitle")}
            </h2>
            <ul className="rpt-act-signoff">
              <li className="rpt-act-signoff__item">
                <span
                  className="rpt-act-signoff__n"
                  style={{ color: stateColourVar("change") }}
                >
                  {a.signoff.pending}
                </span>
                <span>{t("signoffs.tab.pending")}</span>
              </li>
              <li className="rpt-act-signoff__item">
                <span
                  className="rpt-act-signoff__n"
                  style={{ color: stateColourVar("protective") }}
                >
                  {a.signoff.approved}
                </span>
                <span>{t("signoffs.tab.approved")}</span>
              </li>
              <li className="rpt-act-signoff__item">
                <span
                  className="rpt-act-signoff__n"
                  style={{ color: stateColourVar("risk") }}
                >
                  {a.signoff.returned}
                </span>
                <span>{t("signoffs.tab.returned")}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Current at-risk by Pou + domain */}
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">
          {t("reports.activity.currentTitle")}
        </h2>
        <p className="rpt-panel__lead">{t("reports.activity.currentLead")}</p>
        <div className="rpt-act-cols">
          <div>
            <h3 className="rpt-subhead" lang="mi">
              Te Pou Mataaho
            </h3>
            <CurrentStateRows
              groups={a.currentRisk.byPou}
              label={(id) => labels.pou(id)}
            />
          </div>
          <div>
            <h3 className="rpt-subhead">{t("reports.tab.domain")}</h3>
            <CurrentStateRows
              groups={a.currentRisk.byDomain}
              label={(id) => labels.domain(id)}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

export default ActivityView;
